import React, { useState } from "react";
import { changeVendorPassword, validateStrongPassword } from "../Functions/firebaseFunction";

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field] || errors.general) {
      setErrors((prev) => ({ ...prev, [field]: "", general: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccess(false);

    let hasError = false;
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = "Current password is required.";
      hasError = true;
    }
    
    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required.";
      hasError = true;
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password.";
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    const validationError = validateStrongPassword(formData.newPassword);
    if (validationError) {
      setErrors({ newPassword: validationError });
      return;
    }

    if (formData.newPassword === formData.currentPassword) {
      setErrors({ newPassword: "New password must be different from your current password." });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setErrors({ confirmPassword: "New passwords do not match." });
      return;
    }

    setIsLoading(true);
    try {
      await changeVendorPassword(formData.currentPassword, formData.newPassword);
      setSuccess(true);
      setFormData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      
      // Auto close after 2 seconds
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
    } catch (err) {
      // Map common Firebase auth errors to the specific input field
      if (err.message.includes("Incorrect current password")) {
        setErrors({ currentPassword: err.message });
      } else {
        setErrors({ general: err.message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Change Password</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
            disabled={isLoading}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {errors.general && (
            <div className="p-3 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm">
              {errors.general}
            </div>
          )}
          {success && (
            <div className="p-3 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm font-medium">
              Password successfully updated!
            </div>
          )}

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Current Password</label>
            <input
              type="password"
              value={formData.currentPassword}
              onChange={(e) => handleInputChange("currentPassword", e.target.value)}
              className={`w-full p-3 border ${errors.currentPassword ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter current password"
            />
            {errors.currentPassword && <p className="text-xs text-red-500 mt-1">{errors.currentPassword}</p>}
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">New Password</label>
            <input
              type="password"
              value={formData.newPassword}
              onChange={(e) => handleInputChange("newPassword", e.target.value)}
              className={`w-full p-3 border ${errors.newPassword ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter new password"
            />
            {errors.newPassword ? (
              <p className="text-xs text-red-500 mt-1 leading-tight">{errors.newPassword}</p>
            ) : (
              <p className="text-[11px] text-gray-500 mt-1 leading-tight">
                Must be 8+ characters, with 1 uppercase, 1 lowercase, 1 number, and 1 special character.
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Confirm New Password</label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
              className={`w-full p-3 border ${errors.confirmPassword ? "border-red-500" : "border-gray-300"} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Confirm new password"
            />
            {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>}
          </div>

          <div className="pt-4 flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || success}
              className={`flex-1 py-3 rounded-lg text-white font-medium transition-colors ${
                isLoading || success ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isLoading ? "Updating..." : "Update Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
